import React, { useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { CenteredCardContent, StyledLinearProgressBar } from './styles'
import { formatDate } from '../../utils/utils'
import { getPercent } from '../../helpers'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import { green, grey, red, shade, yellow } from '../../colors'
import { Divider, useMediaQuery, useTheme } from '@mui/material'
import { SlotStatus } from '../../models/DistributionPlan'
import CustomTooltip from '../../components/CustomTooltip'
import { type InstructorDistribution } from '../../models/Distribution'
import { useLocation, useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import Chip from '@mui/material/Chip'
import GenericModal from '../../components/GenericModal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AutoImportManager } from '../../APIManager'
import { notification } from '../../components/Notifications'
export interface AdminViewerSurveyListCardProps {
  surveyNumber: number
  totalSurveys: number
  distribution: InstructorDistribution
}
export const AdminViewerSurveyListCard: React.FC<AdminViewerSurveyListCardProps> = ({
  surveyNumber,
  totalSurveys,
  distribution

}): JSX.Element => {
  const [showOptOutConfirmModal, setShowOptOutConfirmModal] = useState<boolean>(false)

  const {
    division,
    endTimestamp,
    startTimestamp,
    releaseTimestamp,
    slotNodeStatus: distSlotNodeStatus,
    completionDetails,
    slotTitle: distSlotTitle,
    isSlotAnonymous: distIsSlotAnonymous,
    slotId,
    divisionId,
    isOptedOut,
    isOptOutable,
    isOptedOutByParent,
    isOptOutSettingEnabled,
    optOutDeadlineTimestamp
  } = distribution || {}

  const courseName = division?.abbreviation
  const sectionAbbr = division?.title
  const endDate = endTimestamp
  const startDate = startTimestamp
  const releaseDate = releaseTimestamp
  const slotNodeStatus = distSlotNodeStatus
  const completionCount = completionDetails?.completionCount
  const studentCount = completionDetails?.studentCount
  const slotTitle = distSlotTitle
  const isSlotAnonymous = distIsSlotAnonymous
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)
  const termId = urlParams.get('termId') ?? ''
  const endDateStr = formatDate(endDate)
  const startDateStr = formatDate(startDate)
  // Format release date as 'Thursday, Jun 19' or show string as-is (e.g., 'Rolling Results')
  let releaseDateStr = '-'
  if (releaseDate) {
    if (typeof releaseDate === 'string' && isNaN(Date.parse(releaseDate))) {
      releaseDateStr = releaseDate
    } else {
      releaseDateStr = formatDate(releaseDate)
    }
  }
  const percentValue = isSlotAnonymous ? 0 : getPercent(completionCount, studentCount)
  const theme = useTheme()
  const questionCount = distribution?.academicLevelAddedQuestions?.questionCount ?? 0
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const params = new URLSearchParams({
    slotId: distribution?.slotId ?? '',
    divisionId: distribution?.division?.divisionId ?? '',
    questionCount: questionCount.toString(),
    categoryName: distribution?.division?.categoryName ?? '',
    isInstructorAddedQuestionsAllowed: distribution?.academicLevelAddedQuestions?.isCustomQuestionAllowed ? 'true' : 'false',
    addedQuestionCount: distribution?.questionCount?.toString() ?? '',
    termId: termId ?? ''
  })

  const renderSurveyDateDetails = (
    start: string,
    end: string
  ): JSX.Element => {
    try {
      switch (slotNodeStatus) {
        case SlotStatus.Draft:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              First Survey begins: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Published:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              First Survey begins: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Live:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Ends: <b>{endDateStr}</b>
            </Typography>
          )
        case SlotStatus.Closed:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Results Released: <b>{releaseDateStr}</b>
            </Typography>
          )
        default:
          return <></>
      }
    } catch (error) {
      return <></>
    }
  }

  // Check if the division category name is included in the academic unit categories
  // This is used to determine if the "Add Questions" button should be enabled or disabled

  const isDivisionCategoryNameIncluded = distribution?.academicLevelAddedQuestions?.academicUnitCategories?.some(
    (item: { categoryName?: string }) => item?.categoryName === distribution?.division?.categoryName
  )

  const isDisabledAddQuestionButton = !distribution.isAcademicLevelAddedQuestionsAllowed || (slotNodeStatus === SlotStatus.Live || slotNodeStatus === SlotStatus.Closed || slotNodeStatus === SlotStatus.Draft) || !isDivisionCategoryNameIncluded

  const addQuestionButtonTooltipTitle = (): string => {
    if (isDisabledAddQuestionButton) {
      return 'The ability to add additional questions is disabled for this Survey'
    }

    return ''
  }

  const optOutButtonTooltipTitle = (): string => {
    if (isOptOutDeadlinePassed) {
      return 'The opt out deadline has passed.'
    }
    return ''
  }
  const { mutate: handleOptoutOrOptIn, isPending: isOptoutOrOptInPinding } = useMutation({
    mutationFn: AutoImportManager.updateOptOutOrOptIn,
    onError: (error: any) => {
      notification(error?.message ?? 'Optout or optIn error', { variant: 'error' })
    },
    meta: { silent: true },

    onSuccess: () => {
      notification(`Successfully ${isOptedOut ? 'opted in for All' : 'opted out for All'}`, { variant: 'success' })
      setShowOptOutConfirmModal(false)
      void queryClient.invalidateQueries({ queryKey: ['getAdminDistributions'] })
    }
  })

  const handleOptOut = (): void => {
    const payload = {
      params: {
        slotId,
        objectId: divisionId
      },
      body: {
        isOptedOut: !isOptedOut
      }
    }
    handleOptoutOrOptIn(payload)
  }

  const isOptOutDeadlinePassed = new Date() > new Date(distribution.optOutDeadlineTimestamp || '')

  return (
    <Card tabIndex={0} aria-label={`survey ${surveyNumber} of ${totalSurveys} ${courseName}`} style={{ marginBottom: '2rem' }}>
      {slotNodeStatus === null &&
        (
          <CenteredCardContent>
            <CircularProgress />
          </CenteredCardContent>
        )
      }

      {slotNodeStatus && (
        <>
          <CardContent >
            <Box display="flex" justifyContent="space-between" >
              <Box>
                <Box display={'flex'} gap={3} alignItems={'center'}>
                  <Typography variant="p4">{slotTitle}</Typography>
                  {isOptOutSettingEnabled && <Typography fontSize={12} fontWeight={600} color={isOptedOut ? red[950] : green[800]}>{isOptedOut ? '• Opted-out' : '• Opted-in'}</Typography>}
                </Box>
                <Box display="flex" alignItems="center" gap={4}>
                  <Typography mt={2} variant="h5" gutterBottom>
                    {sectionAbbr}
                  </Typography>
                  {isSlotAnonymous && (
                    <Chip
                      label="Unverified Submissions"
                      variant="filled"
                      size="small"
                      sx={{
                        borderRadius: '6px',
                        border: `1px solid ${yellow[100]}`,
                        fontWeight: 500,
                        fontSize: '12px',
                        padding: '0px 8px',
                        color: red[800],
                        backgroundColor: shade[1000]
                      }}
                    />
                  )}
                </Box>

                <Typography variant="p4">
                  {courseName}
                </Typography>
              </Box>
              <Divider />
              {
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  label={'View Sections'}
                  fontWeight='600'
                  buttonSize={ButtonSize.Medium}
                  onClick={() => {
                    window.open(`${ROUTES.COURSE_AND_ENROLLMENT}/${distribution?.slotId}?navigate=adminViewerDashboard`, '_blank')
                  }}
                />
              }

            </Box>
          </CardContent>
          <CardContent aria-label={`Survey closed${percentValue}% completed ${completionCount}/${studentCount} Responses`} tabIndex={0}>
            <Box display='flex' justifyContent='space-between'>
              {renderSurveyDateDetails(startDateStr, endDateStr)}
            </Box>
            {isOptOutSettingEnabled && optOutDeadlineTimestamp &&
              <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">Opt-out Deadline: <b>{optOutDeadlineTimestamp && formatDate(optOutDeadlineTimestamp)}</b></Typography>
            }
            <Box padding={theme.spacing(2)}>
              <StyledLinearProgressBar value={percentValue} isNotClosed={slotNodeStatus !== SlotStatus.Closed} isAnonymous={isSlotAnonymous} />
            </Box>
            <Box display='flex' justifyContent='space-between'>
              <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                {percentValue}% Complete
              </Typography>
              <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                {isSlotAnonymous ? `${completionCount} Responses` : `${completionCount}/${studentCount} Responses`}
              </Typography>
            </Box>
            <Divider sx={{ padding: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
              <CustomTooltip title={addQuestionButtonTooltipTitle()}>
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  label={`Add Questions ${typeof distribution?.questionCount === 'number' && distribution.questionCount > 0 ? ` (${distribution.questionCount})` : ''}`}
                  fontWeight='600'
                  buttonSize={ButtonSize.Medium}
                  onClick={() => {
                    navigate(`${ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS}?${params.toString()}`)
                  }}
                  disabled={isDisabledAddQuestionButton}
                />
              </CustomTooltip>
              {isOptOutSettingEnabled &&
               <CustomTooltip title={optOutButtonTooltipTitle()}>
                <GenericButton
                  buttonType={isOptedOut ? ButtonType.Primary : ButtonType.Destructive}
                  label={isOptedOut ? 'Opt In for All' : 'Opt Out for All'}
                  fontWeight='600'
                  buttonSize={ButtonSize.Medium}
                  fullWidth={isMobile}
                  disabled={isOptOutDeadlinePassed || !isOptOutable}
                  onClick={() => { setShowOptOutConfirmModal(true) }}
                />
               </CustomTooltip>
              }

              <GenericModal
                isOpen={showOptOutConfirmModal}
                onClose={() => { setShowOptOutConfirmModal(false) }}
                title={`Confirm ${isOptedOut ? 'Opt-In' : 'Opt-Out'}  for ${sectionAbbr}`}
                confirmButtonLoading={isOptoutOrOptInPinding}
                maxWidth='sm'
                showCloseIcon={false}
                onConfirm={async () => { handleOptOut() }}>
                <Typography mb={1}>
                  Are you sure you want to {isOptedOut ? 'opt in to' : 'opt out of'} this course evaluation? By confirming, you acknowledge that you have permission to do so.
                </Typography>
              </GenericModal>

            </Box>
          </CardContent>
        </>
      )}
    </Card>
  )
}
